import { useState, useEffect } from 'react';
import { bankingAPI } from '../../api';
import SlideOver from '../ui/SlideOver';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Loader2, AlertCircle } from 'lucide-react';

/**
 * TransactionEditForm - Form for editing manual bank transactions
 * 
 * Props:
 * - open: boolean - Controls visibility of the slide-over
 * - onOpenChange: (open: boolean) => void - Callback for open state changes
 * - transaction: object | null - The transaction to edit
 * - onSuccess: (updatedTransaction) => void - Callback after successful save
 */
const TransactionEditForm = ({
  open,
  onOpenChange,
  transaction,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    transaction_type: 'deposit',
    amount: '',
    transaction_date: '',
    reference: '',
    description: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  // Populate form when transaction changes
  useEffect(() => {
    if (transaction && open) {
      setFormData({
        transaction_type: transaction.transaction_type || 'deposit',
        amount: transaction.amount ? parseFloat(transaction.amount).toFixed(2) : '',
        transaction_date: transaction.transaction_date 
          ? new Date(transaction.transaction_date).toISOString().split('T')[0] 
          : '',
        reference: transaction.reference || '',
        description: transaction.description || '',
        notes: transaction.notes || '',
      });
      setError('');
      setValidationErrors({});
    }
  }, [transaction, open]);

  // Reset state when closed
  useEffect(() => {
    if (!open) {
      setError('');
      setValidationErrors({});
    }
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error for this field when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    // Amount validation
    const amount = parseFloat(formData.amount);
    if (!formData.amount || isNaN(amount)) {
      errors.amount = 'Amount is required';
    } else if (amount <= 0) {
      errors.amount = 'Amount must be greater than £0.00';
    }

    // Date validation
    if (!formData.transaction_date) {
      errors.transaction_date = 'Transaction date is required';
    } else {
      const date = new Date(formData.transaction_date);
      if (isNaN(date.getTime())) {
        errors.transaction_date = 'Invalid date';
      }
    }

    // Transaction type validation
    if (!['deposit', 'withdrawal'].includes(formData.transaction_type)) {
      errors.transaction_type = 'Please select a valid transaction type';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!transaction?.id) {
      setError('Transaction ID is missing');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const payload = {
        transaction_type: formData.transaction_type,
        amount: parseFloat(formData.amount),
        transaction_date: formData.transaction_date,
        reference: formData.reference || null,
        description: formData.description || null,
        notes: formData.notes || null,
      };

      const result = await bankingAPI.updateTransaction(transaction.id, payload);
      
      onSuccess?.(result.transaction || result);
      onOpenChange(false);
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to update transaction';
      setError(errorMessage);
      console.error('Error updating transaction:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount || 0);
  };

  return (
    <SlideOver
      open={open}
      onOpenChange={onOpenChange}
      title="Edit Transaction"
      width="w-full max-w-md"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Error Banner */}
        {error && (
          <div className="bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 rounded-[var(--ds-radius-md)] p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-[var(--ds-status-danger)] mt-0.5 flex-shrink-0" />
              <div className="text-sm text-[var(--ds-status-danger)]">
                {error}
              </div>
            </div>
          </div>
        )}

        {/* Transaction Type */}
        <div>
          <Select
            label="Transaction Type"
            name="transaction_type"
            value={formData.transaction_type}
            onChange={handleChange}
            required
            requiredIndicator
            disabled={saving}
            className="h-8"
          >
            <option value="deposit">Deposit (Money In)</option>
            <option value="withdrawal">Withdrawal (Money Out)</option>
          </Select>
          {validationErrors.transaction_type && (
            <p className="mt-1 text-xs text-[var(--ds-status-danger)]">
              {validationErrors.transaction_type}
            </p>
          )}
        </div>

        {/* Amount */}
        <div>
          <Input
            label="Amount (£)"
            type="number"
            name="amount"
            value={formData.amount}
            onChange={handleChange}
            placeholder="0.00"
            step="0.01"
            min="0.01"
            required
            requiredIndicator
            disabled={saving}
            className="h-8"
          />
          {validationErrors.amount && (
            <p className="mt-1 text-xs text-[var(--ds-status-danger)]">
              {validationErrors.amount}
            </p>
          )}
        </div>

        {/* Transaction Date */}
        <div>
          <Input
            label="Transaction Date"
            type="date"
            name="transaction_date"
            value={formData.transaction_date}
            onChange={handleChange}
            required
            requiredIndicator
            disabled={saving}
            className="h-8"
          />
          {validationErrors.transaction_date && (
            <p className="mt-1 text-xs text-[var(--ds-status-danger)]">
              {validationErrors.transaction_date}
            </p>
          )}
        </div>

        {/* Reference */}
        <div>
          <Input
            label="Reference"
            type="text"
            name="reference"
            value={formData.reference}
            onChange={handleChange}
            placeholder="e.g., Cheque #1234, Transfer Ref"
            maxLength={100}
            disabled={saving}
            className="h-8"
          />
          <p className="mt-1 text-xs text-[var(--ds-text-muted)]">
            Optional. A short reference for this transaction.
          </p>
        </div>

        {/* Description */}
        <div>
          <Input
            label="Description"
            type="text"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Brief description of the transaction"
            maxLength={255}
            disabled={saving}
            className="h-8"
          />
        </div>

        {/* Notes */}
        <div>
          <Textarea
            label="Notes"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={3}
            placeholder="Additional notes or details..."
            disabled={saving}
            className="text-xs"
          />
        </div>

        {/* Current Amount Preview (shows impact of change) */}
        {transaction && formData.amount && parseFloat(formData.amount) !== parseFloat(transaction.amount) && (
          <div className="bg-[var(--ds-status-info-bg)] border border-[var(--ds-status-info)]/30 rounded-[var(--ds-radius-md)] p-4">
            <div className="text-sm text-[var(--ds-text-secondary)]">
              <div className="font-medium text-[var(--ds-text-primary)] mb-2">Amount Change</div>
              <div className="flex justify-between items-center">
                <span>Current:</span>
                <span className="font-medium tabular-nums">{formatCurrency(transaction.amount)}</span>
              </div>
              <div className="flex justify-between items-center mt-1">
                <span>New:</span>
                <span className="font-medium tabular-nums">{formatCurrency(parseFloat(formData.amount) || 0)}</span>
              </div>
              <div className="flex justify-between items-center mt-1 pt-2 border-t border-[var(--ds-status-info)]/30">
                <span>Difference:</span>
                <span className={`font-medium tabular-nums ${
                  parseFloat(formData.amount) > parseFloat(transaction.amount)
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-red-600 dark:text-red-400'
                }`}>
                  {parseFloat(formData.amount) > parseFloat(transaction.amount) ? '+' : ''}
                  {formatCurrency(parseFloat(formData.amount) - parseFloat(transaction.amount))}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-[var(--ds-border-divider)]">
          <Button
            variant="outline"
            size="sm"
            type="button"
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            type="submit"
            disabled={saving}
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </form>
    </SlideOver>
  );
};

export default TransactionEditForm;
